import React, { useEffect, useState } from 'react'
import { Collapse, Table } from 'antd'
import { useTranslation } from 'react-i18next'
import { PollutantsResultResponse } from '../../types'
import { Panel } from '../../../ui'
import style from './style.m.less'

interface PollutantsTableProps {
    result: PollutantsResultResponse
    volume: string
}

const impactColumns = [
    { dataIndex: 'first', key: 'first', width: '25%' },
    { dataIndex: 'second', key: 'second', width: '25%' },
    { dataIndex: 'third', key: 'third', width: '25%' },
    { dataIndex: 'fourth', key: 'fourth', width: '25%' },
]

const resultColumns = [
    { dataIndex: 'so2', key: 'so2', width: '25%' },
    { dataIndex: 'zola', key: 'zola', width: '25%' },
    { dataIndex: 'nox', key: 'nox', width: '25%' },
    { dataIndex: 'co', key: 'co', width: '25%' },
]

export const PollutantsTable: React.FC<PollutantsTableProps> = ({ result, volume }) => {
    const { t } = useTranslation()
    const [isMobile, setIsMobile] = useState(false)
    const [isMiniMobile, setIsMiniMobile] = useState(false)

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 900px)')
        const miniMql = window.matchMedia('(max-width: 550px)')

        setIsMobile(mql.matches)
        setIsMiniMobile(miniMql.matches)

        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        const miniHandler = (e: MediaQueryListEvent) => setIsMiniMobile(e.matches)

        mql.addEventListener('change', handler)
        miniMql.addEventListener('change', miniHandler)

        return () => {
            mql.removeEventListener('change', handler)
            miniMql.removeEventListener('change', miniHandler)
        }
    }, [])

    const formatResult = (value: number, precision: number): string => {
        const [integer, decimal] = value.toFixed(precision).split('.')
        const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        return decimal ? `${formattedInteger},${decimal}` : formattedInteger
    }

    const personImpactData = [
        {
            key: 'person-impact',
            first: t('calculator.results.personPanelFirst'),
            second: t('calculator.results.personPanelSecond'),
            third: t('calculator.results.personPanelThird'),
            fourth: t('calculator.results.personPanelFourth'),
        },
    ]

    const natureImpactData = [
        {
            key: 'nature-impact',
            first: t('calculator.results.naturePanelFirst'),
            second: t('calculator.results.naturePanelSecond'),
            third: t('calculator.results.naturePanelThird'),
            fourth: t('calculator.results.naturePanelFourth'),
        },
    ]

    const miniColumns = [
        {
            dataIndex: 'col1',
            key: 'col1',
            width: '100%',
            render: (text: any, record: any) =>
                record.isTitle
                    ? {
                          children: <strong>{text}</strong>,
                          props: {
                              style: {
                                  textAlign: 'center',
                                  background: '#fafafa',
                                  fontSize: 16,
                              },
                          },
                      }
                    : text,
        },
    ]

    const pollutants = [
        {
            id: 'so2',
            img: '/images/SO2.png',
            alt: 'Диоксид серы',
            value: result.k_so2,
            person: personImpactData[0].first,
            nature: natureImpactData[0].first,
            className: style.pollutantImage,
        },
        {
            id: 'zola',
            img: '/images/Zola.png',
            alt: 'Зола твердого топлива',
            value: result.k_zola,
            person: personImpactData[0].second,
            nature: natureImpactData[0].second,
            className: style.pollutantImage,
        },
        {
            id: 'nox',
            img: '/images/NOx.png',
            alt: 'Оксиды азота',
            value: result.k_nox,
            person: personImpactData[0].third,
            nature: natureImpactData[0].third,
            className: style.pollutantImage,
        },
        {
            id: 'co',
            img: '/images/CO.png',
            alt: 'Угарный газ',
            value: result.k_co,
            person: personImpactData[0].fourth,
            nature: natureImpactData[0].fourth,
            className: style.pollutantImage,
        },
    ]

    return (
        <div className={style.resultContainer} id="result_container">
            <p className={style.pollutantsText}>
                {t('calculator.pollutantsTableTextStart')}
                <strong>
                    {volume} {t('calculator.results.mwh')}
                </strong>
                {t('calculator.pollutantsTableTextEnd')}
            </p>
            {isMiniMobile ? (
                <>
                    {pollutants.map((p, index) => (
                        <Table
                            key={p.id}
                            bordered
                            className={style.impactTable}
                            columns={miniColumns}
                            dataSource={[
                                {
                                    key: `image-${p.id}`,
                                    col1: (
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <img alt={p.alt} className={p.className} src={p.img} />
                                        </div>
                                    ),
                                },
                                {
                                    key: `value-${p.id}`,
                                    col1: (
                                        <div style={{ textAlign: 'center' }}>
                                            <span className={style.resultValue}>
                                                {formatResult(p.value, 0)}
                                            </span>{' '}
                                            {t('calculator.results.gram')}
                                        </div>
                                    ),
                                },
                                {
                                    key: `person-title-${p.id}`,
                                    isTitle: true,
                                    col1: t('calculator.results.personPanelLabel'),
                                },
                                {
                                    key: `person-data-${p.id}`,
                                    col1: p.person,
                                },
                                {
                                    key: `nature-title-${p.id}`,
                                    isTitle: true,
                                    col1: t('calculator.results.naturePanelLabel'),
                                },
                                {
                                    key: `nature-data-${p.id}`,
                                    col1: p.nature,
                                },
                            ]}
                            pagination={false}
                            showHeader={false}
                            style={{ marginTop: index > 0 ? 16 : 0 }}
                        />
                    ))}
                </>
            ) : isMobile ? (
                <>
                    <Table
                        bordered
                        className={style.impactTable}
                        columns={[
                            {
                                dataIndex: 'col1',
                                key: 'col1',
                                width: '50%',
                                render: (text: any, record: any) =>
                                    record.isTitle
                                        ? {
                                              children: <strong>{text}</strong>,
                                              props: {
                                                  colSpan: 2,
                                                  style: {
                                                      textAlign: 'center',
                                                      background: '#fafafa',
                                                      fontSize: 16,
                                                  },
                                              },
                                          }
                                        : text,
                            },
                            {
                                dataIndex: 'col2',
                                key: 'col2',
                                width: '50%',
                                render: (text: any, record: any) =>
                                    record.isTitle ? { props: { colSpan: 0 } } : text,
                            },
                        ]}
                        dataSource={[
                            {
                                key: 'images-1',
                                col1: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Диоксид серы"
                                            className={style.pollutantImage}
                                            src="/images/SO2.png"
                                        />
                                    </div>
                                ),
                                col2: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Зола твердого топлива"
                                            className={style.pollutantImage}
                                            src="/images/Zola.png"
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'values-1',
                                col1: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_so2, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                                col2: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_zola, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                            },
                            {
                                key: 'person-title',
                                isTitle: true,
                                col1: t('calculator.results.personPanelLabel'),
                            },
                            {
                                key: 'person-data',
                                col1: personImpactData[0].first,
                                col2: personImpactData[0].second,
                            },
                            {
                                key: 'nature-title',
                                isTitle: true,
                                col1: t('calculator.results.naturePanelLabel'),
                            },
                            {
                                key: 'nature-data',
                                col1: natureImpactData[0].first,
                                col2: natureImpactData[0].second,
                            },
                        ]}
                        pagination={false}
                        showHeader={false}
                    />
                    <Table
                        bordered
                        className={style.impactTable}
                        columns={[
                            {
                                dataIndex: 'col1',
                                key: 'col1',
                                width: '50%',
                                render: (text: any, record: any) =>
                                    record.isTitle
                                        ? {
                                              children: <strong>{text}</strong>,
                                              props: {
                                                  colSpan: 2,
                                                  style: {
                                                      textAlign: 'center',
                                                      background: '#fafafa',
                                                      fontSize: 16,
                                                  },
                                              },
                                          }
                                        : text,
                            },
                            {
                                dataIndex: 'col2',
                                key: 'col2',
                                width: '50%',
                                render: (text: any, record: any) =>
                                    record.isTitle ? { props: { colSpan: 0 } } : text,
                            },
                        ]}
                        dataSource={[
                            {
                                key: 'images-2',
                                col1: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Оксиды азота"
                                            className={style.pollutantImage}
                                            src="/images/NOx.png"
                                        />
                                    </div>
                                ),
                                col2: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Угарный газ"
                                            className={style.pollutantImage}
                                            src="/images/CO.png"
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'values-2',
                                col1: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_nox, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                                col2: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_co, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                            },
                            {
                                key: 'person-title',
                                isTitle: true,
                                col1: t('calculator.results.personPanelLabel'),
                            },
                            {
                                key: 'person-data',
                                col1: personImpactData[0].third,
                                col2: personImpactData[0].fourth,
                            },
                            {
                                key: 'nature-title',
                                isTitle: true,
                                col1: t('calculator.results.naturePanelLabel'),
                            },
                            {
                                key: 'nature-data',
                                col1: natureImpactData[0].third,
                                col2: natureImpactData[0].fourth,
                            },
                        ]}
                        pagination={false}
                        showHeader={false}
                        style={{ marginTop: 16 }}
                    />
                </>
            ) : (
                <>
                    <Table
                        bordered
                        className={style.resultTable}
                        columns={resultColumns}
                        dataSource={[
                            {
                                key: 'images',
                                so2: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Диоксид серы"
                                            className={style.pollutantImage}
                                            src="/images/SO2.png"
                                        />
                                    </div>
                                ),
                                zola: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Зола твердого топлива"
                                            className={style.pollutantImage}
                                            src="/images/Zola.png"
                                        />
                                    </div>
                                ),
                                nox: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Оксиды азота"
                                            className={style.pollutantImageLower}
                                            src="/images/NOx.png"
                                        />
                                    </div>
                                ),
                                co: (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            alt="Угарный газ"
                                            className={style.pollutantImage}
                                            src="/images/CO.png"
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'values',
                                so2: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_so2, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                                zola: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_zola, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                                nox: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_nox, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                                co: (
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={style.resultValue}>
                                            {formatResult(result.k_co, 0)}
                                        </span>{' '}
                                        {t('calculator.results.gram')}
                                    </div>
                                ),
                            },
                        ]}
                        pagination={false}
                        showHeader={false}
                    />
                    <Collapse className={style.impactCollapse}>
                        <Panel
                            header={t('calculator.results.personPanelLabel')}
                            key="person"
                            isSmall
                        >
                            <Table
                                bordered
                                className={style.impactTable}
                                columns={impactColumns}
                                dataSource={personImpactData}
                                pagination={false}
                                showHeader={false}
                            />
                        </Panel>
                        <Panel
                            header={t('calculator.results.naturePanelLabel')}
                            key="nature"
                            isSmall
                        >
                            <Table
                                bordered
                                className={style.impactTable}
                                columns={impactColumns}
                                dataSource={natureImpactData}
                                pagination={false}
                                showHeader={false}
                            />
                        </Panel>
                    </Collapse>
                </>
            )}
            <a className={style.resultLink} href="https://green-e-track.ru/buy_sert/">
                {t('calculator.pollutantsTableLink')}
            </a>
        </div>
    )
}
